import { Link } from "react-router-dom";
import { ArrowLeft, Check, Minus, Printer } from "lucide-react";
import { Lockup, Star } from "../components/brand/Logo";
import { useLang } from "../i18n/LangContext";
import { EMAIL } from "../i18n/strings";

const TIER = {
  essential: "border border-ink/15",
  professional: "bg-[#E4D8FB] border border-[#B794F6]",
  premium: "bg-[#0A0A0A] text-[#F5F1E9]",
};

const SheetPlan = ({ p, rec }) => (
  <div className={`relative flex flex-col rounded-sm p-5 ${TIER[p.key]}`} data-testid={`sheet-plan-${p.key}`}>
    {p.key === "professional" && <span className="label absolute -top-2.5 left-5 rounded-full bg-ink px-3 py-1 text-[8px] text-lav">{rec}</span>}
    <p className="font-display text-lg font-bold uppercase">{p.name}</p>
    <p className="mt-1 text-[10px] leading-snug opacity-70">{p.desc}</p>
    <p className="mt-3 font-display text-4xl font-bold tracking-tighter">{p.price}</p>
    <ul className="mt-4 space-y-1.5">
      {p.items.map((it, j) => (
        <li key={it} className={`flex gap-2 text-[10.5px] leading-snug ${p.key === "premium" && j === 2 ? "font-semibold text-[#B6FF6B]" : ""}`}>
          <Check className="mt-0.5 h-3 w-3 shrink-0" />{it}
        </li>
      ))}
    </ul>
  </div>
);

export default function PricingSheet() {
  const { t, path } = useLang();
  const m = t.maint;
  return (
    <div className="min-h-screen bg-graphite px-4 pb-20 pt-28 md:pt-32">
      <div className="no-print mx-auto mb-8 flex max-w-[816px] items-center justify-between">
        <Link to={path("services")} className="label flex items-center gap-2 text-mist hover:text-ivory" data-testid="sheet-back-link"><ArrowLeft className="h-4 w-4" />{t.sheet.back}</Link>
        <button onClick={() => window.print()} data-testid="sheet-print-button"
          className="label flex items-center gap-2 rounded-full bg-lav px-6 py-3 text-ink transition-colors hover:bg-ivory">
          <Printer className="h-4 w-4" />{t.sheet.print}
        </button>
      </div>
      <article className="print-sheet mx-auto flex min-h-[1056px] max-w-[816px] flex-col bg-ivory p-10 text-ink shadow-2xl" data-testid="pricing-sheet">
        <header className="flex items-start justify-between border-b border-ink/15 pb-6">
          <div className="w-52"><Lockup color="#0A0A0A" accent="#7A5CC4" className="w-full" /></div>
          <div className="text-right">
            <p className="font-display text-xl font-bold uppercase">{t.sheet.title}</p>
            <p className="label mt-1 text-[9px] text-ink/60">{t.common.tagline}</p>
          </div>
        </header>
        <div className="mt-8 grid grid-cols-3 gap-3">
          {t.pricing.plans.map((p) => <SheetPlan key={p.key} p={p} rec={t.pricing.recommended} />)}
        </div>
        <p className="mt-4 text-[10px] leading-snug text-ink/70">{t.common.scopeNote}</p>
        <section className="relative mt-6 overflow-hidden rounded-sm bg-[#0A0A0A] p-6 text-[#F5F1E9]" data-testid="sheet-maintenance">
          <Star className="absolute -right-10 -top-10 h-44 w-44 text-[#B794F6]/10" />
          <div className="relative grid grid-cols-12 gap-4">
            <div className="col-span-4">
              <p className="label text-[9px] text-[#B794F6]">{m.label}</p>
              <p className="mt-2 font-display text-base font-bold uppercase leading-tight">{m.name}</p>
              <p className="mt-3 font-display text-4xl font-bold tracking-tighter">{m.price}<span className="ml-1 text-xs font-normal opacity-60">{m.per}</span></p>
              <span className="mt-3 inline-block rounded-sm bg-[#B6FF6B] px-3 py-1 font-display text-xs font-extrabold uppercase text-ink">{m.free}</span>
            </div>
            <div className="col-span-4">
              <p className="label mb-2 text-[9px] opacity-60">{m.incTitle}</p>
              <ul className="space-y-1">{m.inc.map((x) => <li key={x} className="flex gap-2 text-[10.5px]"><Check className="mt-0.5 h-3 w-3 shrink-0 text-[#B794F6]" />{x}</li>)}</ul>
            </div>
            <div className="col-span-4">
              <p className="label mb-2 text-[9px] opacity-60">{m.excTitle}</p>
              <ul className="space-y-1">{m.exc.map((x) => <li key={x} className="flex gap-2 text-[10.5px] opacity-70"><Minus className="mt-0.5 h-3 w-3 shrink-0" />{x}</li>)}</ul>
            </div>
            <div className="col-span-12 border-t border-white/10 pt-3 text-[9.5px] leading-relaxed opacity-75">{m.quoted} {m.domain} {m.hosting}</div>
          </div>
        </section>
        <footer className="mt-auto flex items-end justify-between border-t border-ink/15 pt-5 text-[10px]">
          <div className="space-y-0.5"><p>{EMAIL}</p><p>starvynt.com</p></div>
          <p className="label text-[8px] text-ink/60">STARVYNT Creative Studio</p>
          <Star className="h-6 w-6 text-[#7A5CC4]" />
        </footer>
      </article>
    </div>
  );
}
